import React, { FC, useCallback, useEffect, useRef, useState } from 'react'
import {
    SingleBarGraphInsight,
    MultiBarGraphInsight,
    colors,
} from '@hazadapt-git/public-core-base'
import { Bar } from 'react-chartjs-2'
import { ChartData } from 'chart.js/dist/types/index'
import {
    AXIS_TITLE_THICKNESS,
    SCROLL_BAR_THICKNESS,
    getBarGraphConfig,
    getLargestBarGraphDimension,
} from '../../lib/utils'
import { makeStyles } from 'tss-react/mui'
import classNames from 'classnames'

export interface SingleBarGraphInsightViewProps
    extends Omit<SingleBarGraphInsight, 'headline'> {
    headline: React.ReactNode
}

export interface MultiBarGraphInsightViewProps
    extends Omit<MultiBarGraphInsight, 'headline'> {
    headline: React.ReactNode
}

export const BarGraphInsightView: FC<
    SingleBarGraphInsightViewProps | MultiBarGraphInsightViewProps
> = (props: SingleBarGraphInsightViewProps | MultiBarGraphInsightViewProps) => {
    const containerRef = useRef<HTMLDivElement>(null)
    const [largestDimension, setLargestDimension] = useState<number>(0)
    const [needsScroll, setNeedsScroll] = useState<boolean>(false)
    const verticalScroll = props.orientation === 'horizontal'
    // Firefox doesn't allow scrollbar manipulation so we use this to edit some styling
    const isFirefox = window.navigator.userAgent.includes('Firefox')

    const getBarGraphData = useCallback(
        (
            data:
                | SingleBarGraphInsightViewProps
                | MultiBarGraphInsightViewProps,
            staticAxis = false
        ): ChartData<'bar', number[], string> => {
            const getCommonProps = (colors: string | string[]) =>
                staticAxis
                    ? { barThickness: 0 }
                    : {
                          backgroundColor: colors,
                          borderColor: colors,
                          borderRadius: 6,
                      }
            if ('bars' in data) {
                const labels = data.bars.map((b) => b.label)
                const values = data.bars.map((b) => b.value)
                const colors = data.bars.map((b) => b.color)
                return {
                    labels,
                    datasets: [
                        {
                            data: values,
                            ...getCommonProps(colors),
                        },
                    ],
                }
            } else {
                return {
                    labels: data.labels,
                    datasets: data.groups.map((group) => ({
                        label: group.name,
                        data: group.values,
                        ...getCommonProps(group.color),
                    })),
                }
            }
        },
        []
    )

    const data = React.useMemo(
        () => ({
            chart: getBarGraphData(props),
            axis: getBarGraphData(props, true),
        }),
        [props, getBarGraphData]
    )

    const { classes: localClasses } = useLocalStyles({
        verticalScroll,
        largestDimension,
        AXIS_TITLE_THICKNESS,
        SCROLL_BAR_THICKNESS,
    })

    useEffect(() => {
        if (!containerRef.current) return

        const handleResize = () => {
            if (!containerRef.current) return

            const dimension = getLargestBarGraphDimension(data.chart.labels)
            setLargestDimension(dimension)

            const scrollable =
                dimension > containerRef.current.clientWidth ||
                dimension > containerRef.current.clientHeight
            setNeedsScroll(scrollable)
        }

        const observer = new ResizeObserver(handleResize)
        observer.observe(containerRef.current)

        handleResize()

        return () => observer.disconnect()
    }, [data.chart.labels])

    return (
        <div
            style={{
                width: '100%',
            }}
        >
            <div ref={containerRef} className={localClasses.container}>
                <div className={localClasses.scrollableGraphContainer}>
                    <div className={localClasses.graph}>
                        <Bar
                            data={data.chart}
                            options={getBarGraphConfig(props, 'scrollable')}
                        />
                    </div>
                </div>
                <div
                    className={classNames(localClasses.staticAxisContainer, {
                        [localClasses.roomForScrollbar]:
                            !isFirefox && needsScroll,
                    })}
                >
                    <Bar
                        data={data.axis}
                        options={getBarGraphConfig(props, 'axis')}
                    />
                </div>
            </div>
        </div>
    )
}

interface StyleProps {
    verticalScroll: boolean
    largestDimension: number
    AXIS_TITLE_THICKNESS: number
    SCROLL_BAR_THICKNESS: number
}

const useLocalStyles = makeStyles<StyleProps>()(
    (
        _,
        {
            verticalScroll,
            largestDimension,
            AXIS_TITLE_THICKNESS,
            SCROLL_BAR_THICKNESS,
        }
    ) => ({
        container: {
            position: 'relative',
            aspectRatio: '1 / 1',
        },
        scrollableGraphContainer: {
            position: 'absolute',
            right: 0,
            zIndex: 1,
            height: verticalScroll
                ? `calc(100% - ${AXIS_TITLE_THICKNESS}px)`
                : '100%',
            width: verticalScroll
                ? '100%'
                : `calc(100% - ${AXIS_TITLE_THICKNESS}px)`,
            // overflowX: verticalScroll ? 'hidden' : 'auto', //TODO revisit this once horizontally scrolling graphs are implemented
            overflowX: 'hidden',
            overflowY: verticalScroll ? 'auto' : 'hidden',
            '&::-webkit-scrollbar': {
                width: `${SCROLL_BAR_THICKNESS}px`,
                height: `${SCROLL_BAR_THICKNESS}px`,
            },
            '&::-webkit-scrollbar-track': {
                background: colors.grays.SILK,
                borderRadius: '.5rem',
            },
            '&::-webkit-scrollbar-thumb': {
                background: colors.grays.NIMBUS,
                borderRadius: '.5rem',
                cursor: 'pointer',
            },
        },
        graph: {
            height: '100%',
            width: '100%',
            minHeight: verticalScroll ? largestDimension : '100%',
            // minWidth: verticalScroll ? '100%' : largestDimension, //TODO revisit this once horizontally scrolling graphs are implemented
        },
        staticAxisContainer: {
            position: 'absolute',
            height: '100%',
            width: '100%',
        },
        roomForScrollbar: {
            height: verticalScroll
                ? '100%'
                : `calc(100% - ${SCROLL_BAR_THICKNESS}px)`,
            width: verticalScroll
                ? `calc(100% - ${SCROLL_BAR_THICKNESS}px)`
                : '100%',
        },
    })
)
